import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import {
  Box,
  Button,
  Checkbox as ChakraCheckbox,
  HStack,
  Icon,
  Input,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import Dialog from "../../../shared/react/src/chakra/Dialog"
import { Link } from "../../../shared/react/src/components/Link"
import { formatAssetClass } from "@hydrologiq/shared/asset/PaginatedTable"
import { SupabaseContext } from "../providers/SystemProvider"
import {
  AddAssetFormProvider,
  useActiveFilter,
  useAddAssetFormActions,
  useIsSelected,
  useSearch,
  useSelectedIds,
} from "../providers/AddAssetFormProvider"
import { RefObject } from "react"
import {
  FaBolt,
  FaBatteryFull,
  FaSolarPanel,
  FaLeaf,
  FaLayerGroup,
  FaMagnifyingGlass,
  FaChevronUp,
  FaPlug,
  FaCheck,
  FaXmark,
} from "react-icons/fa6"
import { IconType } from "react-icons"

// --- Types ---

interface AvailableAsset {
  id: string
  name: string
  class: string | null
  kind: string | null
}

interface ClassGroup {
  classValue: string
  label: string
  count: number
  icon: IconType
}

// --- Asset class icon mapping ---

const CLASS_ICONS: Record<string, IconType> = {
  "generator": FaBolt,
  "battery": FaBatteryFull,
  "solar": FaSolarPanel,
  "fuel storage": FaLeaf,
  "storage": FaBatteryFull,
  "inverter/charger": FaPlug,
  "distro": FaPlug,
  "power": FaBolt,
  "gas": FaLeaf,
  "refueller": FaLeaf,
  "electrolyser": FaBolt,
  "monitoring": FaSolarPanel,
}

function getClassIcon(formattedClass: string): IconType {
  return CLASS_ICONS[formattedClass.toLowerCase()] ?? FaBolt
}

/**
 * CategoryTabs — single-row horizontally scrollable filter pills grouped by asset class (type).
 * Only subscribes to activeFilter — won't re-render on selection changes.
 */
const CategoryTabs = memo(({ assets, disabled }: { assets: AvailableAsset[]; disabled?: boolean }) => {
  const activeFilter = useActiveFilter()
  const { setActiveFilter } = useAddAssetFormActions()
  const scrollRef = useRef<HTMLDivElement>(null)
  const activeButtonRef = useRef<HTMLButtonElement>(null)

  const classGroups = useMemo<ClassGroup[]>(() => {
    const counts = new Map<string, number>()
    assets.forEach((asset) => {
      const classVal = asset.class?.trim() || "other"
      counts.set(classVal, (counts.get(classVal) ?? 0) + 1)
    })
    return Array.from(counts.entries()).map(([classValue, count]) => {
      const label = formatAssetClass(classValue)
      return {
        classValue,
        label,
        count,
        icon: getClassIcon(label),
      }
    })
  }, [assets])

  // Scroll the active tab into view when filter changes
  useEffect(() => {
    if (activeButtonRef.current && scrollRef.current) {
      activeButtonRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      })
    }
  }, [activeFilter])

  return (
    <Box
      ref={scrollRef}
      width="100%"
      overflowX="auto"
      overflowY="hidden"
      whiteSpace="nowrap"
      pb={1}
      css={{
        "&::-webkit-scrollbar": { height: "4px" },
        "&::-webkit-scrollbar-thumb": { background: "#c4c4c4", borderRadius: "2px" },
        "&::-webkit-scrollbar-track": { background: "transparent" },
      }}
    >
      <HStack gap={2} flexWrap="nowrap" width="max-content">
        {/* All tab */}
        <Button
          ref={activeFilter === null ? activeButtonRef : undefined}
          size="sm"
          variant={activeFilter === null ? "hydrologiq_primary" : "hydrologiq_secondary"}
          borderRadius="full"
          onClick={() => !disabled && setActiveFilter(null)}
          disabled={disabled}
          flexShrink={0}
        >
          <Icon as={FaLayerGroup} boxSize={3} />
          All
          <Box
            as="span"
            bg="bg.muted"
            color={"primary"}
            px={1.5}
            borderRadius="full"
            fontSize="xs"
            fontWeight="bold"
          >
            {assets.length}
          </Box>
        </Button>
        {/* Class tabs */}
        {classGroups.map((group) => (
          <Button
            key={group.classValue}
            ref={activeFilter === group.classValue ? activeButtonRef : undefined}
            size="sm"
            variant={activeFilter === group.classValue ? "hydrologiq_primary" : "hydrologiq_secondary"}
            borderRadius="full"
            onClick={() => !disabled && setActiveFilter(group.classValue)}
            disabled={disabled}
            flexShrink={0}
          >
            <Icon as={group.icon} boxSize={3} />
            {group.label}
            <Box
              as="span"
              bg="bg.muted"
              color={"primary"}
              px={1.5}
              borderRadius="full"
              fontSize="xs"
              fontWeight="bold"
            >
              {group.count}
            </Box>
          </Button>
        ))}
      </HStack>
    </Box>
  )
})
CategoryTabs.displayName = "CategoryTabs"

/**
 * SearchField — only subscribes to search state.
 */
const SearchField = memo(({ disabled }: { disabled?: boolean }) => {
  const search = useSearch()
  const { setSearch } = useAddAssetFormActions()

  return (
    <HStack
      width="100%"
      px={3}
      py={2}
      border="1.5px solid"
      borderColor="#e2e8f0"
      borderRadius="md"
      bg="card"
      gap={2}
    >
      <Icon as={FaMagnifyingGlass} color="fg.muted" boxSize={3.5} />
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by name or class..."
        disabled={disabled}
        aria-label="Search assets"
        variant="flushed"
        size="sm"
        border="none"
        _focus={{ boxShadow: "none" }}
      />
    </HStack>
  )
})
SearchField.displayName = "SearchField"

/**
 * SelectAllRow — subscribes to selectedIds for select-all logic.
 * Shows filtered type label when a filter is active.
 */
const SelectAllRow = memo(({
  filteredAssetIds,
  totalTypeCount,
  filterLabel,
  disabled,
}: {
  filteredAssetIds: string[]
  totalTypeCount: number
  filterLabel: string | null
  disabled?: boolean
}) => {
  const selectedIds = useSelectedIds()
  const { selectAll, deselectAll } = useAddAssetFormActions()

  const selectedInView = useMemo(
    () => filteredAssetIds.filter((id) => selectedIds.includes(id)).length,
    [filteredAssetIds, selectedIds],
  )
  const allSelected = filteredAssetIds.length > 0 && selectedInView === filteredAssetIds.length
  const someSelected = selectedInView > 0 && !allSelected

  const totalSelected = selectedIds.length

  const handleToggleAll = useCallback(() => {
    if (disabled) return
    if (allSelected) {
      deselectAll()
    } else {
      selectAll(filteredAssetIds)
    }
  }, [disabled, allSelected, deselectAll, selectAll, filteredAssetIds])

  const summaryText = filterLabel
    ? `${totalTypeCount} types (filtered: ${filterLabel}) · ${totalSelected} selected`
    : `${totalTypeCount} types · ${totalSelected} selected`

  return (
    <HStack
      width="100%"
      px={3}
      py={2}
      borderBottom="1.5px solid"
      borderColor="#e2e8f0"
      justify="space-between"
      bg="bg.subtle"
    >
      <HStack gap={2}>
        <ChakraCheckbox.Root
          checked={allSelected ? true : someSelected ? "indeterminate" : false}
          onCheckedChange={handleToggleAll}
          disabled={disabled}
        >
          <ChakraCheckbox.HiddenInput />
          <ChakraCheckbox.Control />
        </ChakraCheckbox.Root>
        <Text fontSize="sm" fontWeight="medium">Select all</Text>
      </HStack>
      <Text fontSize="xs" color="fg.muted">
        {summaryText}
      </Text>
    </HStack>
  )
})
SelectAllRow.displayName = "SelectAllRow"

/**
 * AssetListItem — uses useIsSelected(id) which only triggers a re-render
 * when THIS specific item's checked state changes, not on every selection change.
 */
const AssetListItem = memo(({
  asset,
  disabled,
}: {
  asset: AvailableAsset
  disabled?: boolean
}) => {
  const checked = useIsSelected(asset.id)
  const { toggleSelected } = useAddAssetFormActions()

  const handleToggle = useCallback(() => {
    if (!disabled) toggleSelected(asset.id)
  }, [disabled, toggleSelected, asset.id])

  const subtitle = formatAssetClass(asset.class, asset.kind)

  return (
    <HStack
      width="100%"
      px={3}
      py={3}
      cursor={disabled ? "default" : "pointer"}
      bg={checked ? "#eaf7fa" : "transparent"}
      _hover={disabled ? undefined : { bg: checked ? "#eaf7fa" : "gray.50" }}
      onClick={handleToggle}
      borderBottom="1px solid"
      borderColor="#e2e8f0"
      gap={3}
    >
      <ChakraCheckbox.Root
        checked={checked}
        onCheckedChange={handleToggle}
        disabled={disabled}
        onClick={(e) => e.stopPropagation()}
      >
        <ChakraCheckbox.HiddenInput />
        <ChakraCheckbox.Control />
      </ChakraCheckbox.Root>

      {/* Name and subtitle */}
      <VStack align="start" gap={0} flex={1} minWidth={0}>
        <HStack gap={1} onClick={(e) => e.stopPropagation()} position="relative" zIndex={1}>
          <Link href={`/assets/${asset.id}`} fontSize="sm" iconSize={9} truncate>
            {asset.name}
          </Link>
        </HStack>
        {subtitle && subtitle !== "n/a" && (
          <Text fontSize="xs" color="fg.muted" truncate>{subtitle}</Text>
        )}
      </VStack>

      {/* Type badge */}
      {asset.class && (
        <Text fontSize="xs" color="fg.muted" flexShrink={0} fontWeight="medium">
          {formatAssetClass(asset.class, asset.kind)}
        </Text>
      )}
    </HStack>
  )
})
AssetListItem.displayName = "AssetListItem"

/**
 * AssetList — renders the filtered list of assets.
 * Does NOT subscribe to selectedIds — each AssetListItem handles its own checked state.
 */
const AssetList = memo(({ filteredAssets, disabled }: { filteredAssets: AvailableAsset[]; disabled?: boolean }) => {
  return (
    <VStack align="stretch" gap={0} maxHeight="320px" overflowY="auto" className="assign-assets-list">
      {filteredAssets.length === 0 ? (
        <Text fontSize="sm" color="fg.muted" py={4} px={3}>No assets match your search.</Text>
      ) : (
        filteredAssets.map((asset) => (
          <AssetListItem
            key={asset.id}
            asset={asset}
            disabled={disabled}
          />
        ))
      )}
    </VStack>
  )
})
AssetList.displayName = "AssetList"

/**
 * AssetListScreen — fetches available (unassigned) assets for the tenant and
 * renders a searchable, filterable multi-select list.
 * Does NOT subscribe to selectedIds — delegates that to child components.
 */
const AssetListScreen = ({ disabled, onAssetsLoaded }: { disabled?: boolean; onAssetsLoaded?: (assets: AvailableAsset[]) => void }) => {
  const supabase = useContext(SupabaseContext)
  const search = useSearch()
  const activeFilter = useActiveFilter()
  const [assets, setAssets] = useState<AvailableAsset[]>([])
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)

  const fetchAvailableAssets = useCallback(async () => {
    if (!supabase) return

    setLoading(true)
    setFetchError(null)

    try {
      // Query the asset_list_view and exclude assets already assigned to a system
      // Also exclude child assets (those with a parent) — only parents should be assignable
      const { data: viewRows, error: assetsError } = await supabase
        .from("asset_list_view")
        .select("asset_id, asset_name, asset_class, asset_kind")
        .or("system_ids.eq.{},system_ids.is.null")
        .is("asset_parent", null)
        .order("asset_name", { ascending: true })

      if (assetsError) {
        setFetchError("Failed to load available assets")
        setLoading(false)
        return
      }

      const availableAssets: AvailableAsset[] = (viewRows ?? []).map((row) => ({
        id: row.asset_id,
        name: row.asset_name,
        class: row.asset_class,
        kind: row.asset_kind,
      }))

      setAssets(availableAssets)
      onAssetsLoaded?.(availableAssets)
    } catch {
      setFetchError("An unexpected error occurred")
    } finally {
      setLoading(false)
    }
  }, [supabase])

  useEffect(() => {
    fetchAvailableAssets()
  }, [fetchAvailableAssets])

  // Filter by active class tab
  const classFilteredAssets = useMemo(() => {
    if (!activeFilter) return assets
    return assets.filter((asset) => (asset.class?.trim() || "other") === activeFilter)
  }, [assets, activeFilter])

  // Filter by search text
  const filteredAssets = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return classFilteredAssets
    return classFilteredAssets.filter((asset) => {
      const haystack = `${asset.name} ${formatAssetClass(asset.class, asset.kind)}`.toLowerCase()
      return haystack.includes(term)
    })
  }, [classFilteredAssets, search])

  // IDs of filtered assets (stable reference for SelectAllRow)
  const filteredAssetIds = useMemo(() => filteredAssets.map((a) => a.id), [filteredAssets])

  // Count of unique asset types for the "X types" label
  const uniqueTypeCount = useMemo(() => {
    const classes = new Set(assets.map((a) => a.class).filter(Boolean))
    return classes.size
  }, [assets])

  // Formatted active filter label for header
  const filterLabel = useMemo(() => {
    if (!activeFilter) return null
    return formatAssetClass(activeFilter)
  }, [activeFilter])

  if (loading) {
    return (
      <HStack justify="center" p={6}>
        <Spinner size="sm" />
        <Text fontSize="sm" color="fg.muted">Loading available assets...</Text>
      </HStack>
    )
  }

  if (fetchError) {
    return <Text fontSize="sm" color="fg.error">{fetchError}</Text>
  }

  if (assets.length === 0) {
    return (
      <VStack p={6} gap={2}>
        <Text fontSize="sm" color="fg.muted">No available assets</Text>
        <Text fontSize="xs" color="fg.subtle">All assets are currently assigned to a system.</Text>
      </VStack>
    )
  }

  return (
    <VStack align="stretch" gap={0} width="100%">
      <CategoryTabs assets={assets} disabled={disabled} />
      <Box mt={3}>
        <SearchField disabled={disabled} />
      </Box>
      <Box mt={3} border="1.5px solid" borderColor="#e2e8f0" borderRadius="md" overflow="hidden" bg="card">
        <SelectAllRow
          filteredAssetIds={filteredAssetIds}
          totalTypeCount={uniqueTypeCount}
          filterLabel={filterLabel}
          disabled={disabled}
        />
        <AssetList filteredAssets={filteredAssets} disabled={disabled} />
      </Box>
    </VStack>
  )
}

// --- Assignment Progress Screen ---

type AssetProgressStatus = "pending" | "assigning" | "done" | "failed"

interface AssetProgressItem {
  id: string
  name: string
  status: AssetProgressStatus
}

const AssignmentProgressScreen = ({ progressRef, registerRender }: { progressRef: React.RefObject<{ items: AssetProgressItem[]; isRefetching: boolean }>; registerRender: React.MutableRefObject<() => void> }) => {
  const [, forceRender] = useState(0)

  // Register our re-render trigger with the parent
  useEffect(() => {
    registerRender.current = () => forceRender((n) => n + 1)
    return () => { registerRender.current = () => {} }
  }, [registerRender])

  const { items, isRefetching } = progressRef.current

  return (
    <VStack align="stretch" gap={3} width="100%" py={4}>
      <Text fontSize="sm" fontWeight="medium" color="fg.muted">
        Assigning assets to system...
      </Text>
      <VStack align="stretch" gap={2} px={2}>
        {items.map((item) => (
          <HStack key={item.id} gap={3} py={1}>
            {item.status === "assigning" && <Spinner size="xs" color="#39aed2" />}
            {item.status === "done" && (
              <Icon as={FaCheck} boxSize={3} color="green.500" />
            )}
            {item.status === "failed" && (
              <Icon as={FaXmark} boxSize={3} color="red.500" />
            )}
            {item.status === "pending" && (
              <Box width="12px" height="12px" borderRadius="full" bg="gray.200" />
            )}
            <Text fontSize="sm" color={item.status === "pending" ? "fg.muted" : "fg.default"}>
              {item.name}
            </Text>
            {item.status === "done" && (
              <Icon as={FaCheck} boxSize={3.5} color="green.500" ml="auto" />
            )}
            {item.status === "failed" && (
              <Text fontSize="xs" color="red.500" ml="auto">Failed</Text>
            )}
          </HStack>
        ))}
      </VStack>

      {/* Refetch step */}
      <HStack gap={3} py={2} mt={2} borderTop="1px solid" borderColor="#e2e8f0" pt={3}>
        {isRefetching ? (
          <Spinner size="xs" color="#39aed2" />
        ) : (
          <Box width="12px" height="12px" borderRadius="full" bg="gray.200" />
        )}
        <Text fontSize="sm" color={isRefetching ? "fg.default" : "fg.muted"}>
          Generating system view...
        </Text>
        {isRefetching && (
          <Text fontSize="xs" color="fg.muted" ml="auto">Please wait</Text>
        )}
      </HStack>
    </VStack>
  )
}
AssignmentProgressScreen.displayName = "AssignmentProgressScreen"

const DialogFooter = memo(({
  onCancel,
  onAssign,
  disabled,
  assetsRef,
}: {
  onCancel: () => void
  onAssign: () => void
  disabled: boolean
  assetsRef: React.RefObject<AvailableAsset[]>
}) => {
  const selectedIds = useSelectedIds()
  const { removeSelected } = useAddAssetFormActions()
  const selectedCount = selectedIds.length
  const [expanded, setExpanded] = useState(false)

  const selectedAssets = useMemo(
    () => selectedIds.map((id) => assetsRef.current.find((a) => a.id === id)).filter(Boolean) as AvailableAsset[],
    [selectedIds, assetsRef]
  )

  // Auto-collapse when nothing is selected
  useEffect(() => {
    if (selectedCount === 0) setExpanded(false)
  }, [selectedCount])

  return (
    <Box width="100%" position="relative">
      {/* Selected assets panel — positioned absolutely so it overlays upward without affecting dialog height */}
      {expanded && selectedAssets.length > 0 && (
        <Box
          position="absolute"
          bottom="100%"
          left={0}
          right={0}
          bg="card"
          borderTop="1px solid"
          borderColor="border"
          boxShadow="0 -2px 8px rgba(0,0,0,0.06)"
          borderTopRadius="md"
          zIndex={10}
        >
          <VStack align="stretch" gap={0} px={4} pt={3} pb={2}>
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color="fg.muted"
              textTransform="uppercase"
              letterSpacing="wider"
              mb={2}
            >
              Selected Assets
            </Text>
            <VStack
              align="stretch"
              gap={0}
              maxHeight="172px"
              overflowY="auto"
            >
              {selectedAssets.map((asset) => (
                <HStack
                  key={asset.id}
                  width="100%"
                  py={2}
                  gap={3}
                  borderBottom="1px solid"
                  borderColor="border.muted"
                  _last={{ borderBottom: "none" }}
                >
                  <Box
                    width="8px"
                    height="8px"
                    borderRadius="full"
                    bg="#39aed2"
                    flexShrink={0}
                  />
                  <Text fontSize="sm" fontWeight="medium" flex={1} truncate>
                    {asset.name}
                  </Text>
                  <Text fontSize="xs" color="fg.muted" flexShrink={0}>
                    {formatAssetClass(asset.class, asset.kind)}
                  </Text>
                  <Button
                    variant="plain"
                    size="xs"
                    color="red.500"
                    fontWeight="semibold"
                    fontSize="xs"
                    flexShrink={0}
                    onClick={() => removeSelected(asset.id)}
                    px={0}
                    height="auto"
                    minWidth="auto"
                    _hover={{ color: "red.600", textDecoration: "underline" }}
                  >
                    Remove
                  </Button>
                </HStack>
              ))}
            </VStack>
          </VStack>
        </Box>
      )}

      {/* Footer bar */}
      <HStack width="100%" justify="space-between" py={2}>
        <HStack
          gap={2}
          cursor={selectedCount > 0 ? "pointer" : "default"}
          onClick={() => {
            console.debug("[DialogFooter] click — selectedCount:", selectedCount, "expanded:", expanded, "selectedAssets:", selectedAssets.length, "assetsRef:", assetsRef.current.length)
            if (selectedCount > 0) setExpanded((prev) => !prev)
          }}
          userSelect="none"
        >
          <Box
            bg="#39aed2"
            color="white"
            borderRadius="full"
            width="28px"
            height="28px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="xs"
            fontWeight="bold"
          >
            {selectedCount}
          </Box>
          <Text fontSize="sm" fontWeight="medium">assets selected</Text>
          {selectedCount > 0 && (
            <Icon
              as={FaChevronUp}
              boxSize={3}
              color="fg.muted"
              transform={expanded ? "rotate(180deg)" : "rotate(0deg)"}
              transition="transform 0.2s"
            />
          )}
        </HStack>
        <HStack gap={2}>
          <Button variant="hydrologiq_secondary" size="sm" onClick={onCancel} disabled={disabled} borderRadius="5px">
            Cancel
          </Button>
          <Button
            variant="hydrologiq_primary"
            size="sm"
            onClick={onAssign}
            disabled={disabled || selectedCount === 0}
            borderRadius="5px"
          >
            Assign
          </Button>
        </HStack>
      </HStack>
    </Box>
  )
})
DialogFooter.displayName = "DialogFooter"

// --- Inner dialog ---

interface AddAssetDialogInnerProps {
  open: boolean
  onClose: () => void
  assignAsset: (assetId: string) => Promise<void>
  onComplete: () => Promise<void>
  loading?: boolean
  container?: RefObject<HTMLElement | null>
}

function AddAssetDialogInner({ open, onClose, assignAsset, onComplete, loading: externalLoading, container }: AddAssetDialogInnerProps) {
  const { reset, setSubmitting, getSelectedIds } = useAddAssetFormActions()
  const [showProgress, setShowProgress] = useState(false)
  // Local submitting state — avoids subscribing to SubmittingContext which would cause
  // unnecessary re-renders when other context consumers update.
  const [localSubmitting, setLocalSubmitting] = useState(false)
  const progressRef = useRef<{ items: AssetProgressItem[]; isRefetching: boolean }>({ items: [], isRefetching: false })
  const progressRenderRef = useRef<() => void>(() => {})
  const assetsRef = useRef<AvailableAsset[]>([])

  const updateProgress = useCallback((updater: (prev: { items: AssetProgressItem[]; isRefetching: boolean }) => void) => {
    updater(progressRef.current)
    progressRenderRef.current()
  }, [])

  const handleClose = useCallback(() => {
    reset()
    progressRef.current = { items: [], isRefetching: false }
    setShowProgress(false)
    setLocalSubmitting(false)
    onClose()
  }, [reset, onClose])

  // Store asset data from the list screen for progress display
  const setAvailableAssets = useCallback((assets: AvailableAsset[]) => {
    assetsRef.current = assets
  }, [])

  const handleConfirm = useCallback(async () => {
    const ids = getSelectedIds()
    if (ids.length === 0) return

    // Build progress items from available asset data
    const items: AssetProgressItem[] = ids.map((id) => {
      const asset = assetsRef.current.find((a) => a.id === id)
      return { id, name: asset?.name || id, status: "pending" as AssetProgressStatus }
    })
    progressRef.current = { items, isRefetching: false }

    // Single state transition: submitting + show progress together
    setLocalSubmitting(true)
    setSubmitting(true)
    setShowProgress(true)

    // Assign each asset sequentially with progress updates
    for (let i = 0; i < ids.length; i++) {
      updateProgress((p) => { p.items[i].status = "assigning" })
      try {
        await assignAsset(ids[i])
        updateProgress((p) => { p.items[i].status = "done" })
      } catch {
        updateProgress((p) => { p.items[i].status = "failed" })
      }
    }

    // Refetch system data
    updateProgress((p) => { p.isRefetching = true })
    await new Promise((resolve) => setTimeout(resolve, 3000))
    await onComplete()
    setSubmitting(false)
    handleClose()
  }, [getSelectedIds, setSubmitting, assignAsset, onComplete, handleClose, updateProgress])

  // Reset form state when dialog closes
  useEffect(() => {
    if (!open) {
      reset()
      progressRef.current = { items: [], isRefetching: false }
      setShowProgress(false)
      setLocalSubmitting(false)
    }
  }, [open, reset])

  // Sync external loading state
  useEffect(() => {
    if (externalLoading !== undefined) {
      setSubmitting(externalLoading)
      setLocalSubmitting(!!externalLoading)
    }
  }, [externalLoading, setSubmitting])

  return (
    <Dialog
      title="Assign Assets"
      description={showProgress ? undefined : "Select the assets that belong to this system"}
      open={open}
      setOpen={(isOpen) => {
        if (!isOpen && !localSubmitting) handleClose()
      }}
      onClose={localSubmitting ? undefined : handleClose}
      container={container}
      size="lg"
      showCloseButton={!localSubmitting}
      footer={
        showProgress ? undefined : (
          <DialogFooter
            onCancel={handleClose}
            onAssign={handleConfirm}
            disabled={localSubmitting}
            assetsRef={assetsRef}
          />
        )
      }
    >
      <VStack align="stretch" gap={0} width="100%" minHeight="400px">
        {showProgress ? (
          <AssignmentProgressScreen progressRef={progressRef} registerRender={progressRenderRef} />
        ) : (
          <AssetListScreen disabled={localSubmitting} onAssetsLoaded={setAvailableAssets} />
        )}
      </VStack>
    </Dialog>
  )
}

// --- Exported component (wraps with provider) ---

interface AddAssetDialogProps {
  open: boolean
  onClose: () => void
  assignAsset: (assetId: string) => Promise<void>
  onComplete: () => Promise<void>
  loading?: boolean
  container?: RefObject<HTMLElement | null>
}

const AddAssetDialog = memo((props: AddAssetDialogProps) => {
  return (
    <AddAssetFormProvider>
      <AddAssetDialogInner {...props} />
    </AddAssetFormProvider>
  )
})

export default AddAssetDialog
